import { useEffect, useState, useRef, useCallback } from 'react';
import api from '../axios';
import { createAccountRedirectUrl } from '../config/runtime';

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const expiryTimerRef = useRef(null);

  const clearExpiryTimer = useCallback(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, []);

  const scheduleTokenExpiry = useCallback((tokenExpiration) => {
    clearExpiryTimer();

    if (!tokenExpiration) return;

    const currentTime = Math.floor(Date.now() / 1000);
    const timeLeft = tokenExpiration - currentTime;

    if (timeLeft <= 0) {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiration');
      setUser(null);
      return false;
    }

    expiryTimerRef.current = setTimeout(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiration');
      setUser(null);
      
      window.location.href = createAccountRedirectUrl('/logout');
    }, timeLeft * 1000);

    return true;
  }, [clearExpiryTimer]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const token = params.get('token');
    const logoutFlag = params.get('logout');
    const redirect = params.get('redirect');

    if (logoutFlag) {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenExpiration');
      clearExpiryTimer();
      setUser(null);

      if (redirect) {
        window.location.href = redirect;
        return;
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (code) {
      validateSessionCode(code);
      return;
    } else if (token) {
      localStorage.setItem('token', token);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const storedToken = localStorage.getItem('token');
    const storedExpiration = localStorage.getItem('tokenExpiration');

    if (storedToken) {
      if (storedExpiration) {
        const isValid = scheduleTokenExpiry(parseInt(storedExpiration, 10));
        if (!isValid) {
          setLoading(false);
          return;
        }
      }

      try {
        const payload = JSON.parse(atob(storedToken.split('.')[1]));
        setUser({ email: payload.username || payload.email });
      } catch (e) {
        localStorage.removeItem('token');
        localStorage.removeItem('tokenExpiration');
        clearExpiryTimer();
      }
    }
    setLoading(false);

    return () => clearExpiryTimer();
  }, [clearExpiryTimer, scheduleTokenExpiry]);

  const validateSessionCode = async (sessionCode) => {
    try {
      const response = await api.get(`/auth/validate-session-code`, {
        params: { code: sessionCode }
      });

      const { token, tokenExpiration } = response.data;
      
      localStorage.setItem('token', token);
      if (tokenExpiration) {
        localStorage.setItem('tokenExpiration', tokenExpiration);
        scheduleTokenExpiry(tokenExpiration);
      }

      // Set user from payload
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({ email: payload.username || payload.email });
      } catch (e) {
        setUser(null);
      }

      // Clean URL by removing code parameter
      const params = new URLSearchParams(window.location.search);
      params.delete('code');
      const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
      window.history.replaceState({}, document.title, newUrl);

      setLoading(false);
    } catch (err) {
      console.error('Session code validation failed:', err);
      
      // 401/403 will be handled by axios interceptor
      // Only handle other errors here
      if (err.response && err.response.status !== 401 && err.response.status !== 403) {
        window.location.href = createAccountRedirectUrl('/');
      }
    }
  };

  const login = () => {
    window.location.href = createAccountRedirectUrl('/');
  };

  const register = () => {
    window.location.href = createAccountRedirectUrl('/register');
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiration');
    clearExpiryTimer();
    setUser(null);

    window.location.href = createAccountRedirectUrl('/logout');
  };

  return {
    user,
    loading,
    login,
    register,
    logout,
  };
}


